import { useState, useCallback } from "react";
import type { SaveLoad } from "../engine/SaveLoad";
import type { VnProgram } from "../engine/Interpreter";
import type { AchievementDef } from "../engine/AchievementEngine";
import { SaveLoadScreen } from "./SaveLoadScreen";
import { SettingsPanel } from "./SettingsPanel";
import { AchievementGallery } from "./AchievementGallery";
import { AuthPanel } from "./AuthPanel";
import { platform } from "../platform";
import type { CatalogueEntry } from "../platform/PlatformAdapter";

interface TitleScreenProps {
  title: string;
  saveLoad: SaveLoad | null;
  program: VnProgram | null;
  selectedScript: string | null;
  onSelectScript: (name: string) => void;
  onNewGame: () => void;
  onLoadComplete: () => void;
  error: string | null;
  loading: boolean;
  achievements?: Record<string, AchievementDef>;
  achievementUnlocked?: Set<string>;
  /** Reachable even fully logged out (req Module 12) — not part of the
   *  session-gated useCatalogue() list below, so merged in separately. */
  preludeEntry?: CatalogueEntry | null;
}

export function TitleScreen({
  title,
  saveLoad,
  program,
  selectedScript,
  onSelectScript,
  onNewGame,
  onLoadComplete,
  error,
  loading,
  achievements = {},
  achievementUnlocked = new Set(),
  preludeEntry = null,
}: TitleScreenProps) {
  const [showLoad, setShowLoad] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showAchievements, setShowAchievements] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const catalogueEntries = platform.useCatalogue();
  const session = platform.useSession();
  // A logged-in account's own catalogue already includes Prelude (req
  // Module 12) — avoid listing it twice.
  const catalogue =
    preludeEntry && !catalogueEntries.some((g) => g.id === preludeEntry.id)
      ? [preludeEntry, ...catalogueEntries]
      : catalogueEntries;

  const handleContinue = useCallback(async () => {
    if (!saveLoad) return;
    const ok = await saveLoad.load("auto");
    if (ok) onLoadComplete();
  }, [saveLoad, onLoadComplete]);

  const disabled = !program || loading;

  const btnStyle = (isDisabled: boolean): React.CSSProperties => ({
    display: "block",
    width: "240px",
    padding: "0.85rem 1.5rem",
    margin: "0 auto 0.75rem",
    background: "transparent",
    border: isDisabled
      ? "1px solid rgba(255, 255, 255, 0.1)"
      : "1px solid rgba(255, 255, 255, 0.25)",
    borderRadius: "8px",
    color: isDisabled ? "#666" : "#eee",
    fontSize: "1.02rem",
    cursor: isDisabled ? "not-allowed" : "pointer",
    transition: "border-color 0.15s, background 0.15s",
    letterSpacing: "0.03em",
    opacity: isDisabled ? 0.35 : 1,
    pointerEvents: isDisabled ? "none" : "auto",
    fontFamily: "var(--theme-font-family, inherit)",
  });

  if (showLoad && saveLoad) {
    return (
      <SaveLoadScreen
        mode="load"
        saveLoad={saveLoad}
        onClose={() => setShowLoad(false)}
        onLoadComplete={onLoadComplete}
      />
    );
  }

  if (showSettings) {
    return <SettingsPanel onClose={() => setShowSettings(false)} />;
  }

  if (showAchievements) {
    return (
      <AchievementGallery
        achievements={achievements}
        unlocked={achievementUnlocked}
        onClose={() => setShowAchievements(false)}
      />
    );
  }

  if (showAuth) {
    return <AuthPanel onClose={() => setShowAuth(false)} />;
  }

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "linear-gradient(180deg, #07070d 0%, #10101d 100%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 200,
        color: "#eee",
        fontFamily: "var(--theme-font-family, sans-serif)",
      }}
    >
      {/* Wonderful ArcTree Logo Icon */}
      <img
        src="/chrome/icons/arctree_icon.png"
        alt="ArcTree Logo"
        style={{
          width: "140px",
          height: "140px",
          marginBottom: "1rem",
          objectFit: "contain",
          filter: "drop-shadow(0 4px 12px rgba(0, 0, 0, 0.5))",
        }}
      />

      {/* Game Title */}
      <h1
        style={{
          fontSize: "2.2rem",
          fontWeight: 400,
          letterSpacing: "0.06em",
          margin: "0 0 2rem",
          opacity: 0.9,
          textAlign: "center",
        }}
      >
        {selectedScript ? `${selectedScript.toUpperCase()}` : title}
      </h1>

      {/* Session status — quiet by design; only ServerPlatform ever has
          something to show here (LocalPlatform is always "authenticated"
          with no email, so this renders nothing for local builds). */}
      {session.status === "anonymous" && (
        <button
          onClick={() => setShowAuth(true)}
          style={{
            background: "none",
            border: "none",
            color: "var(--theme-accent, #7ec8e3)",
            cursor: "pointer",
            fontSize: "0.8rem",
            marginBottom: "1rem",
            padding: 0,
          }}
        >
          Log In / Register
        </button>
      )}
      {session.status === "authenticated" && session.email && (
        <span style={{ fontSize: "0.75rem", opacity: 0.6, marginBottom: "1rem" }}>
          Signed in as {session.email} ·{" "}
          <button
            onClick={() => {
              void platform.logout();
            }}
            style={{
              background: "none",
              border: "none",
              color: "var(--theme-accent, #7ec8e3)",
              cursor: "pointer",
              fontSize: "0.75rem",
              padding: 0,
            }}
          >
            Log Out
          </button>
        </span>
      )}

      {/* Live select game dropdown */}
      <div
        className="theme-panel"
        style={{
          marginBottom: "2rem",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "0.4rem",
          padding: "0.75rem 1.25rem",
        }}
      >
        <span style={{ fontSize: "0.75rem", opacity: 0.6, textTransform: "uppercase", letterSpacing: "0.08em" }}>
          Select Story
        </span>
        <select
          value={selectedScript ?? ""}
          onChange={(e) => onSelectScript(e.target.value)}
          disabled={loading}
          style={{
            background: "rgba(0, 0, 0, 0.5)",
            color: "#eee",
            border: "1px solid rgba(255, 255, 255, 0.15)",
            borderRadius: "6px",
            padding: "0.4rem 0.8rem",
            fontSize: "0.95rem",
            width: "220px",
            cursor: loading ? "not-allowed" : "pointer",
            outline: "none",
            fontFamily: "var(--theme-font-family, inherit)",
          }}
        >
          <option value="" disabled>-- Select a Game --</option>
          {catalogue.map((entry) => (
            <option key={entry.id} value={entry.id} disabled={entry.status !== "available"}>
              {entry.status === "available" ? entry.title : `${entry.title} (${entry.lockReason ?? "locked"})`}
            </option>
          ))}
        </select>
        {loading && (
          <span style={{ fontSize: "0.7rem", color: "var(--theme-accent, #7ec8e3)", marginTop: "0.2rem" }}>
            Loading file...
          </span>
        )}
      </div>

      {/* Error Boundary display (keeps dropdown clickable) */}
      {error && (
        <div
          style={{
            background: "rgba(220, 50, 50, 0.15)",
            border: "1px solid rgba(220, 50, 50, 0.3)",
            borderRadius: "8px",
            padding: "0.75rem 1rem",
            maxWidth: "360px",
            marginBottom: "1.5rem",
            color: "#ff7f7f",
            fontSize: "0.8rem",
            textAlign: "center",
            boxShadow: "0 4px 10px rgba(0, 0, 0, 0.3)",
          }}
        >
          <strong>Error loading game data:</strong>
          <div style={{ marginTop: "0.25rem", maxHeight: "100px", overflowY: "auto", whiteSpace: "pre-wrap" }}>
            {error}
          </div>
        </div>
      )}

      {/* Action Buttons (disabled if no program is loaded) */}
      <button
        onClick={onNewGame}
        disabled={disabled}
        style={btnStyle(disabled)}
        onMouseEnter={(e) => {
          if (!disabled) {
            e.currentTarget.style.borderColor = "rgba(255,255,255,0.6)";
            e.currentTarget.style.background = "rgba(255,255,255,0.05)";
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled) {
            e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)";
            e.currentTarget.style.background = "transparent";
          }
        }}
      >
        New Game
      </button>

      <button
        onClick={handleContinue}
        disabled={disabled || !saveLoad}
        style={btnStyle(disabled || !saveLoad)}
        onMouseEnter={(e) => {
          if (!disabled && saveLoad) {
            e.currentTarget.style.borderColor = "rgba(255,255,255,0.6)";
            e.currentTarget.style.background = "rgba(255,255,255,0.05)";
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled && saveLoad) {
            e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)";
            e.currentTarget.style.background = "transparent";
          }
        }}
      >
        Continue
      </button>

      <button
        onClick={() => setShowLoad(true)}
        disabled={disabled || !saveLoad}
        style={btnStyle(disabled || !saveLoad)}
        onMouseEnter={(e) => {
          if (!disabled && saveLoad) {
            e.currentTarget.style.borderColor = "rgba(255,255,255,0.6)";
            e.currentTarget.style.background = "rgba(255,255,255,0.05)";
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled && saveLoad) {
            e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)";
            e.currentTarget.style.background = "transparent";
          }
        }}
      >
        Load Game
      </button>

      <button
        onClick={() => setShowSettings(true)}
        disabled={disabled}
        style={btnStyle(disabled)}
        onMouseEnter={(e) => {
          if (!disabled) {
            e.currentTarget.style.borderColor = "rgba(255,255,255,0.6)";
            e.currentTarget.style.background = "rgba(255,255,255,0.05)";
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled) {
            e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)";
            e.currentTarget.style.background = "transparent";
          }
        }}
      >
        Settings
      </button>

      {Object.keys(achievements).length > 0 && (
        <button
          onClick={() => setShowAchievements(true)}
          disabled={disabled}
          style={btnStyle(disabled)}
          onMouseEnter={(e) => {
            if (!disabled) {
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.6)";
              e.currentTarget.style.background = "rgba(255,255,255,0.05)";
            }
          }}
          onMouseLeave={(e) => {
            if (!disabled) {
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)";
              e.currentTarget.style.background = "transparent";
            }
          }}
        >
          Achievements
        </button>
      )}

      <span style={{ fontSize: "0.75rem", opacity: 0.3, marginTop: "2rem" }}>
        Version: {program?.version ?? "0.0.0"}
      </span>
    </div>
  );
}
